import "@/App.css";
import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "@/context/AuthContext";
import { TripProvider } from "@/context/TripContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import ErrorBoundary from "@/components/ErrorBoundary";
import { PublicLangProvider } from "@/i18n/public";

// Auth (léger, chargé à la demande)
const Login = lazy(() => import("@/pages/Login"));
const ForgotPassword = lazy(() => import("@/pages/ForgotPassword"));
const ResetPassword = lazy(() => import("@/pages/ResetPassword"));

// Front-Office public (parcours client) — chunk séparé de l'admin
const BookingList = lazy(() => import("@/pages/public/BookingList"));
const BookingForm = lazy(() => import("@/pages/public/BookingForm"));
const WaitlistValidate = lazy(() => import("@/pages/public/WaitlistValidate"));
const TabletCheckin = lazy(() => import("@/pages/TabletCheckin"));

// Back-Office (chargé à la demande, page par page)
const Dashboard = lazy(() => import("@/pages/Dashboard"));
const TripsList = lazy(() => import("@/pages/TripsList"));
const TripForm = lazy(() => import("@/pages/TripForm"));
const ReservationsList = lazy(() => import("@/pages/ReservationsList"));
const Staff = lazy(() => import("@/pages/Staff"));
const Emails = lazy(() => import("@/pages/Emails"));
const Finances = lazy(() => import("@/pages/Finances"));
const Collections = lazy(() => import("@/pages/Collections"));
const BankReconciliation = lazy(() => import("@/pages/BankReconciliation"));
const Budget = lazy(() => import("@/pages/Budget"));
const GroupPayments = lazy(() => import("@/pages/GroupPayments"));
const Planning = lazy(() => import("@/pages/Planning"));
const ClientsCRM = lazy(() => import("@/pages/ClientsCRM"));
const Hotels = lazy(() => import("@/pages/Hotels"));
const Settings = lazy(() => import("@/pages/Settings"));
const Checkin = lazy(() => import("@/pages/Checkin"));

function PageLoader() {
  return (
    <div
      data-testid="page-loader"
      className="flex items-center justify-center min-h-screen bg-background"
    >
      <div className="h-10 w-10 rounded-full border-2 border-muted border-t-primary animate-spin" />
    </div>
  );
}

const protectedRoutes = [
  { path: "/", element: <Dashboard /> },
  { path: "/sejours", element: <TripsList /> },
  { path: "/sejours/nouveau", element: <TripForm /> },
  { path: "/sejours/:tripId", element: <TripForm /> },
  { path: "/reservations", element: <ReservationsList /> },
  { path: "/checkin", element: <Checkin /> },
  { path: "/staff", element: <Staff /> },
  { path: "/planning", element: <Planning /> },
  { path: "/emails", element: <Emails /> },
  { path: "/finances", element: <Finances /> },
  { path: "/encaissements", element: <Collections /> },
  { path: "/rapprochement", element: <BankReconciliation /> },
  { path: "/budget", element: <Budget /> },
  { path: "/groupes", element: <GroupPayments /> },
  { path: "/clients", element: <ClientsCRM /> },
  { path: "/hotels", element: <Hotels /> },
  { path: "/parametres", element: <Settings /> },
];

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Suspense fallback={<PageLoader />}>
          <Routes>
            {/* Auth */}
            <Route path="/login" element={<Login />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            {/* Front-Office (public) */}
            <Route path="/reserver" element={<PublicLangProvider><BookingList /></PublicLangProvider>} />
            <Route path="/reserver/:tripId" element={<PublicLangProvider><BookingForm /></PublicLangProvider>} />
            <Route path="/valider-attente/:token" element={<PublicLangProvider><WaitlistValidate /></PublicLangProvider>} />
            {/* Poste de signature dédié (tablette) — auth propre, hors menu ordinateur */}
            <Route path="/tablette" element={<TabletCheckin />} />
            {/* Back-Office (protégé) */}
            {protectedRoutes.map((r) => (
              <Route
                key={r.path}
                path={r.path}
                element={<ProtectedRoute><TripProvider><ErrorBoundary>{r.element}</ErrorBoundary></TripProvider></ProtectedRoute>}
              />
            ))}
          </Routes>
        </Suspense>
        <Toaster position="bottom-right" richColors />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
